import { useState } from 'react'
import { API_ENDPOINTS } from '../../config/api'
import { Button } from '../ui/Button'
import { EnterpriseOperationalCard } from '../enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

type ProviderRow = {
  id: string
  providerKey: string
  displayName: string
  enabled: boolean
  healthStatus?: string
  apiKeyMasked?: string | null
  hasApiKey?: boolean
  baseUrl?: string | null
  model?: string | null
  ownership?: string
}

type Props = {
  token: string | null
  providerKey: string
  title: string
  defaultBaseUrl: string
  defaultModel: string
  provider: ProviderRow | null
  onSaved: () => void
  t: (key: string, fallback: string) => string
}

/** Organization-managed (BYOK) provider card — never personal/user-managed. */
export default function OrgByokProviderCard({
  token,
  providerKey,
  title,
  defaultBaseUrl,
  defaultModel,
  provider,
  onSaved,
  t,
}: Props) {
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState(provider?.baseUrl || defaultBaseUrl)
  const [model, setModel] = useState(provider?.model || defaultModel)
  const [enabled, setEnabled] = useState(provider?.enabled ?? false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testMsg, setTestMsg] = useState<string | null>(null)

  const headers = token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : undefined

  const save = async () => {
    if (!headers) return
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        providerKey,
        displayName: title,
        baseUrl,
        model,
        enabled,
        localMode: false,
        secureMode: true,
        ownership: 'ORGANIZATION_MANAGED',
      }
      if (apiKey.trim()) body.apiKey = apiKey.trim()
      const url = provider?.id ? API_ENDPOINTS.AI_PROVIDER_BY_ID(provider.id) : API_ENDPOINTS.AI_PROVIDERS
      const method = provider?.id ? 'PATCH' : 'POST'
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || 'Failed to save provider')
      setApiKey('')
      onSaved()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const testConnection = async () => {
    if (!headers || !provider?.id) {
      setError(t('integrations.ai.saveBeforeTest', 'Save the provider configuration before testing.'))
      return
    }
    setTesting(true)
    setError(null)
    setTestMsg(null)
    try {
      const res = await fetch(API_ENDPOINTS.AI_PROVIDER_TEST(provider.id), {
        method: 'POST',
        headers,
        body: '{}',
      })
      const json = await res.json().catch(() => ({}))
      const data = json?.data || {}
      setTestMsg(data?.message || (res.ok ? 'OK' : 'Failed'))
      if (!res.ok) throw new Error(data?.errorMessage || json?.message || 'Test failed')
      onSaved()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  return (
    <EnterpriseOperationalCard title={title}>
      <p className={entStyles.metaRow} style={{ marginBottom: 8 }}>
        {t(
          'integrations.ai.orgByokNote',
          'Organization-managed credential (BYOK). Personal API keys are not supported. LOCAL_ONLY / NO_LLM policies still apply.',
        )}
      </p>
      <p className={entStyles.metaRow}>
        <span>
          {t('integrations.ai.ownership', 'Ownership')}: ORGANIZATION_MANAGED
        </span>
        <span>
          {t('integrations.ai.maskedKey', 'Key')}:{' '}
          {provider?.apiKeyMasked || (provider?.hasApiKey ? '****' : t('integrations.ai.none', 'none'))}
        </span>
      </p>
      <form
        className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <label className="flex flex-col gap-1">
          <span>{t('integrations.ai.apiKey', 'API key')}</span>
          <input
            className="input"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={provider?.hasApiKey ? '•••• (leave blank to keep)' : ''}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span>{t('integrations.ai.baseUrl', 'Base URL')}</span>
          <input className="input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span>{t('integrations.ai.model', 'Model')}</span>
          <input className="input" value={model} onChange={(e) => setModel(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 mt-6">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>{t('integrations.ai.enabled', 'Enabled')}</span>
        </label>
        <div className="md:col-span-2 flex flex-wrap gap-2">
          <Button type="submit" size="small" color="brand" disabled={saving}>
            {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
          <Button type="button" size="small" color="outline" disabled={testing} onClick={() => void testConnection()}>
            {testing
              ? t('integrations.ai.testing', 'Testing…')
              : t('integrations.ai.testConnection', 'Test connection')}
          </Button>
        </div>
      </form>
      {error ? (
        <p className="text-red-600 text-sm" role="alert">
          {error}
        </p>
      ) : null}
      {testMsg ? <p className={entStyles.metaRow}>Test: {testMsg}</p> : null}
    </EnterpriseOperationalCard>
  )
}
